import { useCallback, useMemo, useRef, useState } from "react"
import {
  Animated,
  Linking,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import type { WebViewProps } from "src/components/WebView"
import { SidekickView } from "src/components/SidekickView"
import { WebView } from "src/components/WebView"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { backToRoot } from "src/utils/routerHelpers"

// Distance (px) of directional scroll that takes the input cluster through its
// full collapse range. Smaller = collapses faster.
const COLLAPSE_SCROLL_DISTANCE = 140
// Minimum uniform scale of the cluster when fully collapsed (10%).
const MIN_CLUSTER_SCALE = 0.1
// Progress above which the cluster is considered "collapsed" enough to enable
// the tap-to-expand overlay.
const COLLAPSED_THRESHOLD = 0.15

type WebViewNavigation = Parameters<NonNullable<WebViewProps["onNavigationStateChange"]>>[0]
export default function WebBrowserScreen() {
  const { url: urlParam } = useLocalSearchParams<{
    url?: string | string[]
  }>()
  const resolvedUrl = Array.isArray(urlParam) ? urlParam[0] : urlParam
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const {
    theme: { colors },
  } = useAppTheme()

  const themedAlert = useMemo(
    () => ({
      card: { backgroundColor: colors.background, shadowColor: colors.shadow },
      title: { color: colors.text },
      body: { color: colors.textDim },
      button: { backgroundColor: colors.buttonBackground },
      buttonText: { color: colors.menuButtonText },
    }),
    [colors],
  )

  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  // WebView's ref type differs between native/web and is awkward to type precisely here.
  // We only use a couple imperative methods (goBack/goForward) so keep this permissive.
  const webViewRef = useRef<any>(null)

  const [canGoBack, setCanGoBack] = useState(false)
  const [canGoForward, setCanGoForward] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [embeddingError, setEmbeddingError] = useState(false)

  // ── Native: scroll-driven collapse (Safari-style) ──────────────────────────
  // collapseProgress goes 0 (fully expanded) → 1 (fully collapsed). We derive a
  // uniform scale (100% → 10%) and opacity (100% → 0%) from it. Scrolling the
  // page down increases progress; scrolling up decreases it, at any depth.
  const collapseProgress = useRef(new Animated.Value(0)).current
  const collapseProgressValue = useRef(0)
  const lastScrollY = useRef(0)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const clusterScale = useRef(
    collapseProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [1, MIN_CLUSTER_SCALE],
    }),
  ).current
  const clusterOpacity = useRef(
    collapseProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 0],
    }),
  ).current

  const setCollapseProgress = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(1, next))
      collapseProgressValue.current = clamped
      collapseProgress.setValue(clamped)
      setIsCollapsed(clamped > COLLAPSED_THRESHOLD)
    },
    [collapseProgress],
  )

  const expandControls = useCallback(() => {
    collapseProgressValue.current = 0
    setIsCollapsed(false)
    Animated.timing(collapseProgress, {
      toValue: 0,
      duration: 200,
      useNativeDriver: false,
    }).start()
  }, [collapseProgress])

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const y = e.nativeEvent.contentOffset.y
      const dy = y - lastScrollY.current
      lastScrollY.current = y
      if (dy === 0) return
      setCollapseProgress(collapseProgressValue.current + dy / COLLAPSE_SCROLL_DISTANCE)
    },
    [setCollapseProgress],
  )

  const handleModeChange = useCallback(
    (mode: "idle" | "talk" | "type") => {
      // Bring the cluster back to full size when the user starts typing
      // so the input isn't stuck shrunken after scrolling.
      if (mode === "type") expandControls()
    },
    [expandControls],
  )

  // Unlock orientation for web browser
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.unlockAsync().catch(() => {})
      // Don't re-lock on cleanup to prevent jitter when auto-navigating between media
    }, []),
  )

  // Handle navigation state changes from WebView
  const handleNavigationStateChange = useCallback((navState: WebViewNavigation) => {
    setCanGoBack(navState.canGoBack)
    setCanGoForward(navState.canGoForward)
  }, [])

  // Go back in web history
  const handleGoBack = useCallback(() => {
    if (canGoBack && webViewRef.current) {
      webViewRef.current.goBack()
    }
  }, [canGoBack])

  // Go forward in web history
  const handleGoForward = useCallback(() => {
    if (canGoForward && webViewRef.current) {
      webViewRef.current.goForward()
    }
  }, [canGoForward])

  const handleEmbeddingError = useCallback(() => {
    setEmbeddingError(true)
  }, [])

  const handleOpenInNewTab = useCallback(() => {
    if (!resolvedUrl) return
    Linking.openURL(resolvedUrl)
  }, [resolvedUrl])

  if (!resolvedUrl) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>No URL provided.</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <StatusBar style="dark" />

      {/* Close button - above SidekickView */}
      <View
        style={[
          styles.closeButton,
          {
            top: effectiveTopInset + 22,
            right: insets.right + 12,
          },
        ]}
      >
        <TouchableOpacity
          onPress={async () => {
            // Unmount AvatarOverlay if in landscape to prevent crash during orientation change (APP-738)
            if (isLandscape) {
              setIsClosing(true)
              // Wait for React to unmount the component
              await new Promise((resolve) => setTimeout(resolve, 0))
            }
            backToRoot()
          }}
          style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close web browser"
        >
          <MaterialIcons name="close" size={20} color={colors.buttonText} />
        </TouchableOpacity>
      </View>

      {!isClosing && (
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          darkBackground={false}
          showControlsBarBackground={false}
          controlsOpacity={clusterOpacity}
          controlsScale={clusterScale}
          controlsVisible={true}
          controlsCollapsed={isCollapsed}
          onExpandControls={expandControls}
          onModeChange={handleModeChange}
          riveOwner="video"
          // Back/forward navigation is not possible for cross-origin iframes on
          // web (the browser blocks all contentWindow.history access), so the
          // navigation buttons are only shown on native where react-native-webview
          // handles history correctly.
          controlsConfig={{
            type: "navigation",
            canGoBack,
            canGoForward,
            onGoBack: handleGoBack,
            onGoForward: handleGoForward,
          }}
        >
          {/* Web content */}
          <View style={styles.webViewContainer}>
            <WebView
              ref={webViewRef}
              source={{ uri: resolvedUrl }}
              style={styles.webView}
              onNavigationStateChange={handleNavigationStateChange}
              onEmbeddingError={handleEmbeddingError}
              onScroll={handleScroll as any}
              scrollEventThrottle={16}
              javaScriptEnabled
              domStorageEnabled
              startInLoadingState
              scalesPageToFit
              allowsInlineMediaPlayback
              mediaPlaybackRequiresUserAction={false}
              // Allow fullscreen video (Android needs this explicitly)
              allowsFullscreenVideo
              // Enable nested scrolling for proper touch handling in nested views
              nestedScrollEnabled
              // Disable third-party cookies to avoid OkHttp cookie parsing crash on Android
              thirdPartyCookiesEnabled={false}
              // Use hardware layer type on Android to enable hardware acceleration
              androidLayerType={Platform.OS === "android" ? "hardware" : undefined}
              // Handle external links
              onShouldStartLoadWithRequest={() => true}
              // Handle errors gracefully
              onError={(syntheticEvent) => {
                const { nativeEvent } = syntheticEvent
                console.warn("WebView error:", nativeEvent)
              }}
            />
            {embeddingError && (
              <View style={styles.embeddingErrorOverlay}>
                <View style={[styles.embeddingErrorCard, themedAlert.card]}>
                  <Text style={[styles.embeddingErrorTitle, themedAlert.title]}>
                    This website can&apos;t be displayed here
                  </Text>
                  <Text style={[styles.embeddingErrorBody, themedAlert.body]}>
                    This website has blocked embedding. You can open it in a new tab instead.
                  </Text>
                  <View style={styles.embeddingErrorActions}>
                    <TouchableOpacity
                      onPress={handleOpenInNewTab}
                      style={[styles.embeddingErrorButton, themedAlert.button]}
                      accessibilityRole="button"
                    >
                      <Text style={[styles.embeddingErrorButtonText, themedAlert.buttonText]}>
                        Open in New Tab
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={backToRoot}
                      style={[styles.embeddingErrorButton, themedAlert.button]}
                      accessibilityRole="button"
                    >
                      <Text style={[styles.embeddingErrorButtonText, themedAlert.buttonText]}>
                        Close
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            )}
          </View>
        </SidekickView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#FFFFFF",
  },
  errorText: {
    color: "#333333",
    textAlign: "center",
  },
  closeButton: {
    position: "absolute",
    zIndex: 2100,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  webViewContainer: {
    flex: 1,
  },
  webView: {
    flex: 1,
  },
  embeddingErrorOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 10,
  },
  embeddingErrorCard: {
    borderRadius: 16,
    padding: 24,
    maxWidth: 360,
    width: "100%",
    alignItems: "center",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  embeddingErrorTitle: {
    fontSize: 17,
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 10,
  },
  embeddingErrorBody: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
    marginBottom: 20,
  },
  embeddingErrorActions: {
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
  embeddingErrorButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    alignItems: "center",
  },
  embeddingErrorButtonText: {
    fontWeight: "500",
    fontSize: 15,
  },
})
